import React, { useEffect, useState } from 'react';
import {
  Table, Card, Typography, Tag, Button, Modal, Form, Input, Select, message, Space, Popconfirm, Switch
} from 'antd';
import { EditOutlined, PlusOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import api from '@/api/client';

const { Title, Text } = Typography;
const { Option } = Select;

interface UserBenefit {
  id: number;
  benefit_key: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  is_active: number;
  created_at: string;
  updated_at: string;
}

const CATEGORY_OPTIONS = [
  { value: 'storage', label: '存储', color: 'blue' },
  { value: 'daily_quota', label: '每日额度', color: 'green' },
  { value: 'feature', label: '功能权益', color: 'purple' },
  { value: 'discount', label: '折扣权益', color: 'orange' },
];

export default function UserBenefitsPage() {
  const [benefits, setBenefits] = useState<UserBenefit[]>([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState<{ open: boolean; benefit: UserBenefit | null }>({ open: false, benefit: null });
  const [form] = Form.useForm();

  const loadBenefits = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/user-benefits');
      setBenefits(data);
    } catch (err) {
      message.error('加载权益列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadBenefits(); }, []);

  const showCreate = () => {
    setModal({ open: true, benefit: null });
    form.resetFields();
  };

  const showEdit = (benefit: UserBenefit) => {
    setModal({ open: true, benefit });
    form.setFieldsValue({
      benefit_key: benefit.benefit_key,
      name: benefit.name,
      description: benefit.description,
      category: benefit.category,
      icon: benefit.icon,
      is_active: benefit.is_active === 1,
    });
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const data = { ...values, is_active: values.is_active ? 1 : 0 };
      
      if (modal.benefit) {
        await api.put(`/user-benefits/${modal.benefit.id}`, data);
      } else {
        await api.post('/user-benefits', data);
      }
      
      message.success(modal.benefit ? '更新成功' : '创建成功');
      setModal({ open: false, benefit: null });
      loadBenefits();
    } catch (err: any) {
      message.error(err.response?.data?.error || '操作失败');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/user-benefits/${id}`);
      message.success('删除成功');
      loadBenefits();
    } catch (err) {
      message.error('删除失败');
    }
  };

  const getCategoryColor = (category: string) => {
    const option = CATEGORY_OPTIONS.find(o => o.value === category);
    return option?.color || 'default';
  };

  const getCategoryLabel = (category: string) => {
    const option = CATEGORY_OPTIONS.find(o => o.value === category);
    return option?.label || category;
  };

  const columns = [
    {
      title: '权益标识',
      dataIndex: 'benefit_key',
      width: 150,
    },
    {
      title: '权益名称',
      dataIndex: 'name',
      width: 150,
      render: (v: string, record: UserBenefit) => (
        <Space>
          <span style={{ fontSize: 18 }}>{record.icon}</span>
          <span>{v}</span>
        </Space>
      ),
    },
    {
      title: '分类',
      dataIndex: 'category',
      width: 120,
      render: (v: string) => <Tag color={getCategoryColor(v)}>{getCategoryLabel(v)}</Tag>,
    },
    {
      title: '描述',
      dataIndex: 'description',
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      width: 80,
      render: (v: number) => <Tag color={v === 1 ? 'green' : 'red'}>{v === 1 ? '启用' : '禁用'}</Tag>,
    },
    {
      title: '操作',
      width: 150,
      render: (_: any, record: UserBenefit) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => showEdit(record)}>编辑</Button>
          <Popconfirm title="确定删除该权益？" onConfirm={() => handleDelete(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>权益管理</Title>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={showCreate}>创建权益</Button>
        <Button icon={<ReloadOutlined />} onClick={loadBenefits}>刷新</Button>
      </Space>
      <Table
        columns={columns}
        dataSource={benefits}
        rowKey="id"
        loading={loading}
        pagination={false}
      />

      {/* 创建/编辑弹窗 */}
      <Modal
        title={modal.benefit ? '编辑权益' : '创建权益'}
        open={modal.open}
        onCancel={() => setModal({ open: false, benefit: null })}
        onOk={handleSave}
        width={500}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="benefit_key" label="权益标识" rules={[{ required: true, pattern: /^[a-z_]+$/, message: '只能使用小写字母和下划线' }]}>
            <Input disabled={!!modal.benefit} placeholder="如: storage_space" />
          </Form.Item>
          <Form.Item name="name" label="权益名称" rules={[{ required: true }]}>
            <Input placeholder="如: 存储空间" />
          </Form.Item>
          <Form.Item name="category" label="分类" rules={[{ required: true }]}>
            <Select placeholder="选择分类">
              {CATEGORY_OPTIONS.map(opt => (
                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="icon" label="图标（emoji）">
            <Input placeholder="如: 💾" />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={2} placeholder="权益描述" />
          </Form.Item>
          <Form.Item name="is_active" label="状态" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="禁用" defaultChecked />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
